/**
 * Network Status Indicator Components
 *
 * Visual indicators for network connectivity status
 *
 * @module components/network/network-status-indicator
 */

'use client';

import React, { useEffect, useState } from 'react';
import {
  Wifi,
  WifiOff,
  Signal,
  SignalHigh,
  SignalMedium,
  SignalLow,
  CloudOff,
  Cloud,
} from 'lucide-react';
import { useNetworkStatus } from '@/lib/hooks/use-network-status';
import { useSyncQueue } from '@/lib/hooks/use-db';

// ============================================================================
// Badge Component (if not already exists)
// ============================================================================

interface BadgeProps {
  children: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info';
  className?: string;
}

function Badge({ children, variant = 'default', className = '' }: BadgeProps) {
  const variants = {
    default: 'bg-gray-100 text-gray-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-yellow-100 text-yellow-800',
    error: 'bg-red-100 text-red-800',
    info: 'bg-blue-100 text-blue-800',
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${variants[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

// ============================================================================
// Network Status Indicator - Compact Badge for Header
// ============================================================================

export function NetworkStatusIndicator() {
  const network = useNetworkStatus();
  const { pendingCount } = useSyncQueue();

  // Get icon based on network type and status
  const getIcon = () => {
    if (network.status === 'offline') {
      return <WifiOff className="h-4 w-4" />;
    }

    switch (network.type) {
      case 'wifi':
      case 'ethernet':
        return <Wifi className="h-4 w-4" />;
      case '4g':
        return <SignalHigh className="h-4 w-4" />;
      case '3g':
        return <SignalMedium className="h-4 w-4" />;
      case '2g':
        return <SignalLow className="h-4 w-4" />;
      default:
        return <Signal className="h-4 w-4" />;
    }
  };

  // Get badge variant
  const getVariant = (): 'success' | 'warning' | 'error' => {
    switch (network.status) {
      case 'online':
        return 'success';
      case 'slow':
        return 'warning';
      case 'offline':
        return 'error';
      default:
        return 'error';
    }
  };

  // Get label text
  const getLabel = () => {
    if (network.status === 'offline') {
      return 'Offline';
    }

    if (network.status === 'slow') {
      return 'Slow';
    }

    return 'Online';
  };

  return (
    <Badge variant={getVariant()} className="flex items-center gap-1.5">
      {getIcon()}
      <span className="hidden sm:inline">{getLabel()}</span>
      {network.type !== 'unknown' && network.status !== 'offline' && (
        <span className="hidden md:inline text-xs opacity-75">
          ({network.type.toUpperCase()})
        </span>
      )}
      {pendingCount > 0 && (
        <span className="ml-1 bg-white bg-opacity-30 px-1.5 py-0.5 rounded text-xs">
          {pendingCount}
        </span>
      )}
    </Badge>
  );
}

// ============================================================================
// Detailed Network Status - For Settings/Debug
// ============================================================================

export function DetailedNetworkStatus() {
  const network = useNetworkStatus();

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Network Status</h3>
        <NetworkStatusIndicator />
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="text-gray-500">Connection</p>
          <p className="font-medium text-gray-900">{network.status}</p>
        </div>

        <div>
          <p className="text-gray-500">Type</p>
          <p className="font-medium text-gray-900">{network.type}</p>
        </div>

        {network.downlink !== undefined && (
          <div>
            <p className="text-gray-500">Download Speed</p>
            <p className="font-medium text-gray-900">{network.downlink.toFixed(1)} Mbps</p>
          </div>
        )}

        {network.rtt !== undefined && (
          <div>
            <p className="text-gray-500">Latency</p>
            <p className="font-medium text-gray-900">{network.rtt} ms</p>
          </div>
        )}

        {network.effectiveType && (
          <div>
            <p className="text-gray-500">Effective Type</p>
            <p className="font-medium text-gray-900">{network.effectiveType}</p>
          </div>
        )}

        {network.saveData !== undefined && (
          <div>
            <p className="text-gray-500">Data Saver</p>
            <p className="font-medium text-gray-900">
              {network.saveData ? 'Enabled' : 'Disabled'}
            </p>
          </div>
        )}
      </div>

      <div className="pt-3 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Last checked: {new Date(network.lastChecked).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// Offline Banner - Persistent Banner When Offline
// ============================================================================

export function OfflineBanner() {
  const network = useNetworkStatus();
  const { pendingCount } = useSyncQueue();
  const [isVisible, setIsVisible] = useState(network.status === 'offline');

  useEffect(() => {
    setIsVisible(network.status === 'offline');
  }, [network.status]);

  if (!isVisible) {
    return null;
  }

  return (
    <div data-testid="offline-toast" className="bg-red-50 border-b border-red-200">
      <div className="max-w-7xl mx-auto py-2 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap">
          <div className="flex items-center gap-2">
            <CloudOff className="h-5 w-5 text-red-600" />
            <p className="text-sm font-medium text-red-800">
              You are currently offline
            </p>
          </div>
          {pendingCount > 0 && (
            <p className="text-sm text-red-700">
              {pendingCount} {pendingCount === 1 ? 'item' : 'items'} pending sync
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Slow Connection Banner
// ============================================================================

export function SlowConnectionBanner() {
  const network = useNetworkStatus();
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Reset dismissed state when connection improves
    if (network.status !== 'slow') {
      setIsDismissed(false);
    }
  }, [network.status]);

  if (network.status !== 'slow' || isDismissed) {
    return null;
  }

  return (
    <div className="bg-yellow-50 border-b border-yellow-200">
      <div className="max-w-7xl mx-auto py-2 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SignalLow className="h-5 w-5 text-yellow-600" />
            <p className="text-sm font-medium text-yellow-800">
              Slow connection detected. Some features may be limited.
            </p>
          </div>
          <button
            onClick={() => setIsDismissed(true)}
            className="text-sm text-yellow-700 hover:text-yellow-900 font-medium"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Connection Restored Toast
// ============================================================================

export function ConnectionRestoredToast() {
  const [isVisible, setIsVisible] = useState(false);
  const network = useNetworkStatus();

  useEffect(() => {
    // Show toast when connection is restored
    if (network.status === 'online' && network.isOnline) {
      setIsVisible(true);

      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [network.status, network.isOnline]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 animate-slide-up">
      <div className="bg-green-600 text-white rounded-lg shadow-lg p-4 flex items-center gap-3 max-w-md">
        <Cloud className="h-5 w-5 flex-shrink-0" />
        <div className="flex-1">
          <p className="font-medium">Connection restored</p>
          <p className="text-sm text-green-100">You're back online</p>
        </div>
        <button
          onClick={() => setIsVisible(false)}
          className="text-green-100 hover:text-white"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Network Status Manager - Combines All Components
// ============================================================================

export function NetworkStatusManager() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return null;
  }

  return (
    <>
      <OfflineBanner />
      <SlowConnectionBanner />
      <ConnectionRestoredToast />
    </>
  );
}
